import type {
  AdditionalForcesAndMomentsSection,
  BuriedSection,
  ChecksLongData,
  ChecksSection,
  ComplianceChecksSection,
  ComponentDetailsSection,
  DefectDetailsSection,
  DiameterEncompassingEntireThinDefectSection,
  ExternalPressureVacuumSection,
  ExtraLayersSection,
  JobDetailsSection,
  OperatingConditionsSection,
  PinholeDetailsData,
  PressureCyclesSection,
  SingleComponentData,
  SlotDetailsData,
  SlotPinholeSection,
  SlotPinholeSectionType,
  UTReadingSection,
} from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { CompositeJob, CreateUpdateCompositeJobSections } from "@stryker/core/types/jobs";
import type {
  AdditionalJobDetailsSection,
  ContactInformationSection,
  DesignConditionsSection,
  NotesSection,
} from "@stryker/core/types/sections";
import { isEmpty } from "remeda";

const hasNonEmptyString = (value: unknown) => !!value?.toString()?.trim();

const getJobDetails = (job: CompositeJob): JobDetailsSection => {
  const jobNumberForForm = job.job_number ?? null;
  const hasRequiredData =
    hasNonEmptyString(jobNumberForForm) &&
    hasNonEmptyString(job.branch) &&
    hasNonEmptyString(job.plant_name) &&
    hasNonEmptyString(job.service_name) &&
    hasNonEmptyString(job.design_code) &&
    !!job.repair_system &&
    !!job.surface_preparation;
  const shouldMarkDone = !!job.is_draft && hasRequiredData;

  return {
    status: shouldMarkDone ? "done" : "open",
    isDataFilled: shouldMarkDone,
    data: {
      branch: job.branch,
      job_number: jobNumberForForm,
      plant_name: job.plant_name,
      service_name: job.service_name,
      design_code: job.design_code ?? null,
      repair_system: job.repair_system,
      surface_preparation: job.surface_preparation,
    },
  };
};

const getAdditionalJobDetails = (
  job: CompositeJob,
  designConditionsFilled: boolean,
  additionalJobDetailsAreFilled: boolean
): AdditionalJobDetailsSection => {
  let status: ExpandStatus = "blocked";
  if (additionalJobDetailsAreFilled || designConditionsFilled) {
    status = "done";
    // check if we have job details section
  } else if (job.plant_name) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: additionalJobDetailsAreFilled || designConditionsFilled,
    addMore: additionalJobDetailsAreFilled,
    data: {
      unit: job.unit,
      line_id: job.line_id,
      salesman: job.salesman,
    },
  };
};

const getContactInformation = ({
  job,
  contactInfoFilled,
  additionalJobDetailsAreFilled,
  designConditionsFilled,
}: {
  job: CompositeJob;
  additionalJobDetailsAreFilled: boolean;
  contactInfoFilled: boolean;
  designConditionsFilled: boolean;
}): ContactInformationSection => {
  let status: ExpandStatus = "blocked";
  if (contactInfoFilled || designConditionsFilled) {
    status = "done";
  } else if (additionalJobDetailsAreFilled || job.plant_name) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: contactInfoFilled || designConditionsFilled,
    addMore: contactInfoFilled,
    data: {
      contact_info_name: job.contact_info_name,
      contact_info_email: job.contact_info_email,
      contact_info_phone: job.contact_info_phone,
    },
  };
};

const getDesignConditions = ({
  job,
  designConditionsFilled,
  contactInfoFilled,
}: {
  job: CompositeJob;
  designConditionsFilled: boolean;
  contactInfoFilled: boolean;
}): DesignConditionsSection => {
  let status: ExpandStatus = "blocked";
  if (designConditionsFilled) {
    status = "done";
  } else if (contactInfoFilled || job.plant_name) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: designConditionsFilled,
    data: {
      design_pressure: job.design_pressure,
      design_temperature: job.design_temperature,
      unit_for_design_temperature: job.unit_for_design_temperature,
    },
  };
};

const getOperatingConditions = ({
  job,
  operatingConditionsFilled,
  designConditionsFilled,
}: {
  job: CompositeJob;
  operatingConditionsFilled: boolean;
  designConditionsFilled: boolean;
}): OperatingConditionsSection => {
  let status: ExpandStatus = "blocked";
  if (operatingConditionsFilled) {
    status = "done";
  } else if (designConditionsFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: operatingConditionsFilled,
    data: {
      operating_pressure: job.operating_pressure,
      unit_for_installation_temperature: job.unit_for_installation_temperature,
      operating_temp_temperature: job.operating_temp_temperature,
      unit_for_operating_temp_temperature: job.unit_for_operating_temp_temperature,
      installation_temperature: job.installation_temperature,
    },
  };
};

const getComponentDetails = ({
  job,
  operatingConditionsFilled,
  componentDetailsFilled,
}: {
  job: CompositeJob;
  operatingConditionsFilled: boolean;
  componentDetailsFilled: boolean;
}): ComponentDetailsSection => {
  let status: ExpandStatus = "blocked";
  if (componentDetailsFilled) {
    status = "done";
  } else if (operatingConditionsFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: componentDetailsFilled,
    data: {
      component_material: job.component_material,
      component_options: job.component_options ?? [],
      components_data: job.components_data as SingleComponentData[],
    },
  };
};

const getDefectDetails = ({
  job,
  componentDetailsFilled,
  defectDetailsFilled,
}: {
  job: CompositeJob;
  componentDetailsFilled: boolean;
  defectDetailsFilled: boolean;
}): DefectDetailsSection => {
  let status: ExpandStatus = "blocked";
  if (defectDetailsFilled) {
    status = "done";
  } else if (componentDetailsFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: defectDetailsFilled,
    data: {
      defects: job.defects as any,
    },
  };
};

const getComplianceChecks = ({
  job,
  complianceChecksFilled,
  defectDetailsFilled,
}: {
  job: CompositeJob;
  complianceChecksFilled: boolean;
  defectDetailsFilled: boolean;
}): ComplianceChecksSection => {
  let status: ExpandStatus = "blocked";
  if (complianceChecksFilled) {
    status = "done";
  } else if (defectDetailsFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: complianceChecksFilled,
    data: {
      desired_layer_count_options: job.desired_layer_count_options,
    },
  };
};

const booleanToPolar = (value?: boolean | null) => {
  if (value === true) return "yes";
  if (value === false) return "no";
  return undefined;
};

const getChecksSection = ({
  job,
  complianceChecksFilled,
}: {
  job: CompositeJob;
  complianceChecksFilled: boolean;
}): ChecksSection => {
  const baseStatus: ExpandStatus = complianceChecksFilled ? "canBeOpened" : "blocked";

  const isNonCompliantRecommended =
    job.desired_layer_count_options?.length > 0 && !job.desired_layer_count_options.includes("none");

  if (isNonCompliantRecommended) {
    const hasDesiredLayerCount = typeof job.desired_layer_count === "number";
    return {
      status: hasDesiredLayerCount ? "done" : baseStatus,
      isDataFilled: hasDesiredLayerCount,
      data: {
        desired_layer_count: hasDesiredLayerCount ? job.desired_layer_count : null,
      },
    };
  }

  const longData: ChecksLongData = {
    do_you_have_ut_reading_info: booleanToPolar(job.do_you_have_ut_reading_info),
    calculate_at_worst_case: booleanToPolar(job.calculate_at_worst_case),
    is_ut_reading_less_than_04: booleanToPolar(job.is_ut_reading_less_than_04),
    per_code_line_leaking_do_you_know_diameter: booleanToPolar(job.per_code_line_leaking_do_you_know_diameter),
    component_leaking: booleanToPolar(job.component_leaking),
    is_pinhole_leak: booleanToPolar(job.is_pinhole_leak),
    component_patched_or_repaired: booleanToPolar(job.component_patched_or_repaired),
    is_component_shutdown_during_repair: booleanToPolar(job.is_component_shutdown_during_repair),
    add_extraLayers: booleanToPolar(job.add_extraLayers),
    do_you_wish_to_answer_more_questions: booleanToPolar(job.do_you_wish_to_answer_more_questions),
    is_component_buried: booleanToPolar(job.is_component_buried),
    design_axial_loads: booleanToPolar(job.design_axial_loads),
    is_component_straight_line: booleanToPolar(job.is_component_straight_line),
    do_you_want_to_take_into_account_any_additional_forces_and_moments: booleanToPolar(
      job.do_you_want_to_take_into_account_any_additional_forces_and_moments
    ),
    do_you_want_to_design_for_external_loads_or_vacuum: booleanToPolar(
      job.do_you_want_to_design_for_external_loads_or_vacuum
    ),
    do_you_want_to_design_for_constant_pressure_cycles: booleanToPolar(
      job.do_you_want_to_design_for_constant_pressure_cycles
    ),
    are_the_expected_cycles_more_than_7000_over_design_life: booleanToPolar(
      job.are_the_expected_cycles_more_than_7000_over_design_life
    ),
    do_you_want_to_design_for_continous_long_term_strains: booleanToPolar(
      job.do_you_want_to_design_for_continous_long_term_strains
    ),
    do_you_want_to_completely_exclude_the_components_strength_for_design: booleanToPolar(
      job.do_you_want_to_completely_exclude_the_components_strength_for_design
    ),
  };

  const hasData = Object.values(longData).some((value) => value !== undefined);

  return {
    status: hasData ? "done" : baseStatus,
    isDataFilled: hasData,
    data: longData,
  };
};

const getSlotPinholeDetails = (job: CompositeJob): SlotPinholeSection | undefined => {
  const slotDetails: SlotDetailsData[] =
    job.slot_details?.map((slot) => ({
      slot_diameter: (slot as any).slot_diameter ?? (job as any).slot_diameter,
    })) || [];

  if (slotDetails.length === 0 && (job as any).slot_diameter) {
    slotDetails.push({ slot_diameter: (job as any).slot_diameter });
  }

  const pinholeDetails: PinholeDetailsData[] =
    job.pinhole_details?.map((pinhole) => ({
      pinhole_diameter: pinhole.pinhole_diameter,
    })) || [];

  if (pinholeDetails.length === 0 && job.pinhole_diameter) {
    pinholeDetails.push({
      pinhole_diameter: job.pinhole_diameter,
    });
  }

  if (slotDetails.length === 0 && pinholeDetails.length === 0) {
    return undefined;
  }

  const sectionType: SlotPinholeSectionType = slotDetails.length > 0 ? "slot" : "pinhole";

  return {
    status: "done",
    isDataFilled: true,
    type: sectionType,
    data: sectionType === "slot" ? slotDetails[0] : pinholeDetails[0],
  };
};

const getBuriedDetails = (job: CompositeJob): BuriedSection | undefined => {
  return job.is_component_buried
    ? {
        status: job.is_component_buried ? "done" : "hidden",
        isDataFilled: true,
        data: {
          buried_depth: null,
          unit_for_buried_check: null,
        },
      }
    : undefined;
};

const getDiameterEncompassingEntireThinDefectSection = (
  job: CompositeJob
): DiameterEncompassingEntireThinDefectSection | undefined => {
  const pinhole2 = job.pinhole_2 ?? (job as any).diameter_encompassing_entire_thin_defect;
  return pinhole2
    ? {
        status: "done",
        isDataFilled: true,
        data: {
          pinhole_2: Number(pinhole2),
        },
      }
    : undefined;
};

const getUtReadingSection = (job: CompositeJob): UTReadingSection | undefined => {
  const utReadings = job.ut_readings ?? (job as any).ut_reading;
  return utReadings
    ? {
        status: "done",
        isDataFilled: true,
        data: {
          ut_readings: Number(utReadings),
        },
      }
    : undefined;
};

const getExtraLayers = (job: CompositeJob): ExtraLayersSection | undefined => {
  return job.extra_layers
    ? {
        status: job.extra_layers ? "done" : "hidden",
        isDataFilled: true,
        data: {
          extra_layers: job.extra_layers ?? "",
        },
      }
    : undefined;
};

const getPressureCyclesSection = (job: CompositeJob): PressureCyclesSection | undefined => {
  return job.pressure_cycles
    ? {
        status: job.pressure_cycles ? "done" : "hidden",
        isDataFilled: true,
        data: {
          number_of_cycles: null,
          min_cycle_pressure: null,
          max_cycle_pressure: null,
        },
      }
    : undefined;
};

const getAdditionalForcesAndMomentsSection = (job: CompositeJob): AdditionalForcesAndMomentsSection | undefined => {
  return job.do_you_want_to_design_for_additional_forces_and_moments
    ? {
        status: job.do_you_want_to_design_for_additional_forces_and_moments ? "done" : "hidden",
        isDataFilled: true,
        data: {
          applied_sheared_load: null,
          applied_axial_load: null,
          applied_tortional_moment: null,
          applied_axial_moment: null,
        },
      }
    : undefined;
};

const getExternalPressureVacuumSection = (job: CompositeJob): ExternalPressureVacuumSection | undefined => {
  return job.do_you_want_to_design_for_external_loads_or_vacuum
    ? {
        status: job.do_you_want_to_design_for_external_loads_or_vacuum ? "done" : "hidden",
        isDataFilled: true,
        data: {
          external_pressure_vacuum: job.external_pressure_vacuum ?? "",
        },
      }
    : undefined;
};

const getNotes = (job: CompositeJob): NotesSection => {
  const hasNotes = !!job.additional_notes.length;
  return {
    status: hasNotes ? "done" : "canBeOpened",
    isDataFilled: hasNotes,
    data: {
      additional_notes: job.additional_notes,
      new_notes: [],
    },
  };
};

const getRepairOptions = ({ job, defectDetailsFilled }: { job: CompositeJob; defectDetailsFilled: boolean }) => {
  let status: ExpandStatus = "blocked";

  // Backend returns/expects option names as strings; keep them as-is.
  const hasValidOptions = job.repair_length_options.length > 0;

  if (defectDetailsFilled) {
    status = hasValidOptions ? "done" : "canBeOpened";
  }

  return {
    status,
    isDataFilled: hasValidOptions,
    data: {
      repair_length_options: job.repair_length_options,
    },
  };
};

const getRepairKits = ({ job, defectDetailsFilled }: { job: CompositeJob; defectDetailsFilled: boolean }) => {
  let status: ExpandStatus = "blocked";
  if (defectDetailsFilled) {
    status = job.repair_length_repair_kit_sizes.length ? "done" : "canBeOpened";
  }
  return {
    status,
    isDataFilled: job.repair_length_repair_kit_sizes.length > 0,
    data: {
      repair_length_repair_kit_sizes: job.repair_length_repair_kit_sizes,
    },
  };
};

export function createInitialState(job: CompositeJob): CreateUpdateCompositeJobSections {
  const jobDetails = getJobDetails(job);
  const designConditionsFilled = !!job.unit_for_design_temperature;

  const additionalJobDetailsAreFilled = !!(job.unit || job.line_id || job.salesman);
  const additionalJobDetails = getAdditionalJobDetails(job, designConditionsFilled, additionalJobDetailsAreFilled);

  const contactInfoFilled = !!(job.contact_info_email || job.contact_info_phone || job.contact_info_name);
  const contactInformation = getContactInformation({
    job,
    contactInfoFilled,
    additionalJobDetailsAreFilled,
    designConditionsFilled,
  });

  const designConditions = getDesignConditions({
    job,
    designConditionsFilled,
    contactInfoFilled,
  });

  const operatingConditionsFilled = !!(
    job.operating_pressure ||
    job.operating_temp_temperature ||
    job.installation_temperature ||
    job.unit_for_installation_temperature ||
    job.unit_for_operating_temp_temperature
  );
  const operatingConditions = getOperatingConditions({ job, operatingConditionsFilled, designConditionsFilled });

  const componentDetailsFilled = !!(
    job.component_material ||
    !isEmpty(job.component_options) ||
    !isEmpty(job.components_data)
  );
  const componentDetails = getComponentDetails({ job, componentDetailsFilled, operatingConditionsFilled });

  const defectDetailsFilled = !isEmpty(job.defects);
  const defectDetails = getDefectDetails({ defectDetailsFilled, componentDetailsFilled, job });

  const complianceChecksFilled = !isEmpty(job.desired_layer_count_options);
  const complianceChecks = getComplianceChecks({ job, complianceChecksFilled, defectDetailsFilled });

  const checksSection = getChecksSection({ job, complianceChecksFilled });

  // Update checksSection status based on data
  if (checksSection.status === "canBeOpened" && checksSection.isDataFilled) {
    checksSection.status = "done";
  }

  const slotPinholeDetails = getSlotPinholeDetails(job);

  const buriedDetails = getBuriedDetails(job);

  const diameterEncompassingEntireThinDefectSection = getDiameterEncompassingEntireThinDefectSection(job);

  const utReadingSection = getUtReadingSection(job);

  const extraLayers = getExtraLayers(job);

  const pressureCyclesSection = getPressureCyclesSection(job);

  const additionalForcesAndMomentsSection = getAdditionalForcesAndMomentsSection(job);

  const externalPressureVacuumSection = getExternalPressureVacuumSection(job);
  const notes = getNotes(job);
  const repairOptions = getRepairOptions({ job, defectDetailsFilled });
  const repairKits = getRepairKits({ job, defectDetailsFilled });

  return {
    jobDetails,
    additionalJobDetails,
    contactInformation,
    designConditions,
    operatingConditions,
    componentDetails,
    defectDetails,
    repairOptions,
    repairKits,
    complianceChecks,
    checksSection,
    slotPinholeDetails,
    diameterEncompassingEntireThinDefectSection,
    utReadingSection,
    pressureCyclesSection,
    additionalForcesAndMomentsSection,
    externalPressureVacuumSection,
    buriedDetails,
    extraLayers,
    notes,
  };
}
